import {
  IconChevronLeft,
  IconCopy,
  IconDots,
  IconMail,
  IconPencil,
  IconSettings,
  IconUser,
} from '@tabler/icons-react';
import {
  APP_ROUTES,
  getFullName,
  getSubEntityLabel,
} from '@zorro/common-utils';
import { ActionIcon } from '@zorro/design-system/v2/ActionIcon';
import { Button } from '@zorro/design-system/v2/Button';
import { Link } from '@zorro/design-system/v2/Link';
import { Menu } from '@zorro/design-system/v2/Menu';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { getRoleLabel } from '@zorro/types';
import {
  Box,
  Group,
  Stack,
  Text,
  brand,
  useDisclosure,
} from '@zorro/web/design-system';
import {
  createImpersonationSession,
  showErrorNotification,
  showSuccessNotification,
  useAppRouter,
  useMonolithQuery,
  useRoles,
  useTextOverflow,
} from '@zorro/web/ui-utils';
import escape from 'lodash/escape';

import { useAnalytics } from '../../Analytics';
import { CopyToClipboard } from '../../CopyToClipboard';
import { CopyableTooltip } from '../../CopyableTooltip';
import { EligibilityStatusBadge } from '../../EligibilityStatusBadge';
import { EmployeeActionsOverlays } from '../../EmployeeActions';
import { resetPassword } from '../../EmployeeActions/AccountManagement/accountManagement.utils';
import { ActionsSubMenu } from '../../EmployeeActions/ActionsSubMenu';
import { EmployeeLayoutAction } from '../EmployeeLayoutActions';
import { getEmployeeRole } from '../EmployeeLayoutUtils';
import { EmployeeLayoutSidebarHeaderSkeleton } from './EmployeeLayoutSidebarHeaderSkeleton';

const EmployerName = ({
  employerName,
  companyOverviewUrl,
  isNonZorroEnrollmentAgent,
}: {
  employerName: string;
  companyOverviewUrl: string;
  isNonZorroEnrollmentAgent: boolean;
}) => {
  const { sendEvent } = useAnalytics();
  const { isTextOverflowing, textRef } =
    useTextOverflow<HTMLElement>(employerName);

  return (
    <CopyableTooltip isDisabled={!isTextOverflowing}>
      <CopyableTooltip.Target>
        {isNonZorroEnrollmentAgent ? (
          <Text ref={textRef} size="xs" truncate="end" maw="100%">
            {employerName}
          </Text>
        ) : (
          <Link
            labelRef={textRef}
            href={companyOverviewUrl}
            underline="always"
            shouldTruncate
            fz="xs"
          >
            {employerName}
          </Link>
        )}
      </CopyableTooltip.Target>
      <CopyableTooltip.Content>
        <CopyableTooltip.Item>
          <CopyableTooltip.Label>Company</CopyableTooltip.Label>
          <CopyableTooltip.Value>{employerName}</CopyableTooltip.Value>
          <CopyableTooltip.Copy
            text={employerName}
            successMessage="Company name copied to clipboard"
            onCopy={() => void sendEvent('copy_company_name')}
          />
        </CopyableTooltip.Item>
      </CopyableTooltip.Content>
    </CopyableTooltip>
  );
};

type Props = {
  employerId: string;
  employeeId: string;
  isDon?: boolean;
  setAction: (action: EmployeeLayoutAction) => void;
};

export const EmployeeLayoutSidebarHeader = ({
  employerId,
  employeeId,
  isDon = false,
  setAction,
}: Props) => {
  const { isZorroOperations, isNonZorroEnrollmentAgent } = useRoles();
  const { sendEvent } = useAnalytics();
  const { navigateToRoute, router } = useAppRouter();
  const [opened] = useDisclosure(false);

  const { data: employee, isLoading: isLoadingEmployee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const { data: employer } = useMonolithQuery({
    method: 'employersControllerFindOne',
    params: [employerId],
  });

  const { data: userRoles, isLoading: isLoadingRoles } = useMonolithQuery({
    method: 'usersControllerGetUserRoles',
    params: employee && [employee.userId],
  });

  const { data: employerSubEntities = [] } = useMonolithQuery({
    method: 'subEntityControllerFindMany',
    params: employee?.employerId && [employee.employerId],
  });

  const { data: employerSubEntities2 = [] } = useMonolithQuery({
    method: 'subEntity2ControllerFindMany',
    params: employee?.employerId && [employee.employerId],
  });

  const isSubEntity2Enabled = employerSubEntities2.length > 0;

  const employerName = employer?.name || 'N/A';

  if (!employee || !userRoles || isLoadingEmployee || isLoadingRoles) {
    return <EmployeeLayoutSidebarHeaderSkeleton />;
  }

  const role = getEmployeeRole(userRoles);
  const subEntity = employerSubEntities.find(
    (subEntity) => subEntity.id === employee.subEntityId
  );
  const subEntity2 = employerSubEntities2.find(
    (subEntity) => subEntity.id === employee.subEntity2Id
  );
  const employeeName = getFullName(employee);

  // Copy the page the user is looking at, so the link reopens that exact tab.
  const employeePageUrl = `${window.location.origin}${router.asPath}`;

  const companyOverviewUrl = isDon
    ? APP_ROUTES.DON_COMPANY()
    : APP_ROUTES.OPS_EMPLOYERS_OVERVIEW(employerId);

  return (
    <Stack
      bg="zorroWhite.0"
      px="md"
      py="xl"
      mb="lg"
      gap={0}
      top={0}
      pos="sticky"
      style={{
        borderRadius: 'var(--mantine-radius-md)',
        boxShadow: 'var(--shadow-card)',
        zIndex: 5,
      }}
    >
      <Group justify="space-between" align="baseline" wrap="nowrap">
        <Group gap="xs" align="flex-start" wrap="nowrap">
          <CopyableTooltip>
            <CopyableTooltip.Target>
              <Text fw={600} size="sm" truncate="end" maw="10.5rem">
                {employeeName}
              </Text>
            </CopyableTooltip.Target>
            <CopyableTooltip.Content>
              <CopyableTooltip.Item>
                <CopyableTooltip.Label>Zorro ID</CopyableTooltip.Label>
                <CopyableTooltip.Value>
                  {employee.shortId}
                </CopyableTooltip.Value>
                <CopyableTooltip.Copy
                  text={employee.shortId}
                  successMessage="Zorro ID copied to clipboard"
                  onCopy={() => void sendEvent('copy_employee_id')}
                />
              </CopyableTooltip.Item>
            </CopyableTooltip.Content>
          </CopyableTooltip>

          <Box>
            <CopyToClipboard
              text={employeePageUrl}
              html={`<a href="${escape(employeePageUrl)}">${escape(
                employeeName
              )}</a>`}
              onCopy={(_text, result) => {
                if (result === 'failed') {
                  showErrorNotification({
                    message: 'Employee URL could not be copied to clipboard',
                  });
                  return;
                }

                void sendEvent('copy_employee_url');
                showSuccessNotification({
                  message: 'Employee URL copied to clipboard',
                });
              }}
            >
              <IconCopy size={12} cursor="pointer" color={brand.zorroGray400} />
            </CopyToClipboard>
          </Box>
        </Group>
        <Box data-testid="userMenu">
          <Menu size="sm" position="bottom-start">
            <Menu.Target>
              <ActionIcon isChecked={opened} size="sm" mt="xxs">
                <IconDots size={18} color={brand.zorroCoal900} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <ActionsSubMenu title="Email communication" icon={IconMail}>
                <Menu.Item
                  onClick={() => {
                    void sendEvent('employee_actions', {
                      action_category: 'Email communication',
                      selected_action: 'EMAIL_ACCOUNT_INVITATION',
                      navigation: 'employee actions',
                    });
                    setAction(EmployeeLayoutAction.EMAIL_ACCOUNT_INVITATION);
                  }}
                >
                  {EmployeeActionsOverlays.EMAIL_ACCOUNT_INVITATION}
                </Menu.Item>
              </ActionsSubMenu>
              <ActionsSubMenu title="Employee administration" icon={IconUser}>
                <Menu.Item
                  onClick={() => {
                    void sendEvent('employee_actions', {
                      action_category: 'Employee administration',
                      selected_action: 'TERMINATE_EMPLOYEE',
                      navigation: 'employee actions',
                    });
                    setAction(EmployeeLayoutAction.TERMINATE_EMPLOYEE);
                  }}
                >
                  {EmployeeActionsOverlays.TERMINATE_EMPLOYEE}
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    void sendEvent('employee_actions', {
                      action_category: 'Employee administration',
                      selected_action: 'SET_LEAVE_OF_ABSENCE',
                      navigation: 'employee actions',
                    });
                    setAction(EmployeeLayoutAction.SET_LEAVE_OF_ABSENCE);
                  }}
                >
                  {EmployeeActionsOverlays.SINGLE_SET_LEAVE_OF_ABSENCE}
                </Menu.Item>
                {isZorroOperations && (
                  <Menu.Item
                    onClick={() => {
                      void sendEvent('employee_actions', {
                        action_category: 'Employee administration',
                        selected_action: 'UPDATE_ELIGIBILITY',
                        navigation: 'eligibility actions',
                      });
                      setAction(EmployeeLayoutAction.UPDATE_ELIGIBILITY);
                    }}
                  >
                    {EmployeeActionsOverlays.UPDATE_ELIGIBILITY}
                  </Menu.Item>
                )}
              </ActionsSubMenu>
              <ActionsSubMenu title="Account management" icon={IconSettings}>
                <Menu.Item
                  onClick={() => {
                    void sendEvent('employee_actions', {
                      action_category: 'Account management',
                      selected_action: 'EDIT_EMPLOYEE_ROLE',
                      navigation: 'employee actions',
                    });
                    setAction(EmployeeLayoutAction.EDIT_EMPLOYEE_ROLE);
                  }}
                >
                  {EmployeeActionsOverlays.CHANGE_EMPLOYEE_ROLE}
                </Menu.Item>

                <Menu.Item
                  onClick={() => {
                    void sendEvent('employee_actions', {
                      action_category: 'Account management',
                      selected_action: 'CREATE_IMPERSONATION_SESSION',
                      navigation: 'employee actions',
                    });
                    createImpersonationSession(employeeId, APP_ROUTES.HOME());
                  }}
                >
                  Log in as
                </Menu.Item>
                {isZorroOperations && (
                  <Menu.Item
                    onClick={async () => {
                      void sendEvent('employee_actions', {
                        action_category: 'Account management',
                        selected_action: 'RESET_PASSWORD',
                        navigation: 'employee actions',
                      });
                      await resetPassword(employee.userId);
                    }}
                  >
                    Reset password
                  </Menu.Item>
                )}
                <Menu.Item
                  onClick={() => {
                    void sendEvent('employee_actions', {
                      action_category: 'Account management',
                      selected_action: 'EDIT_COMPANY_EMAIL',
                      navigation: 'employee actions',
                    });
                    setAction(EmployeeLayoutAction.EDIT_COMPANY_EMAIL);
                  }}
                >
                  Edit company email
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    void sendEvent('employee_actions', {
                      action_category: 'Account management',
                      selected_action: 'DELETE_EMPLOYEE',
                      navigation: 'employee actions',
                    });
                    setAction(EmployeeLayoutAction.DELETE_EMPLOYEE);
                  }}
                >
                  Delete employee
                </Menu.Item>
              </ActionsSubMenu>
            </Menu.Dropdown>
          </Menu>
        </Box>
      </Group>
      <Stack gap="xs" pb="md">
        <EmployerName
          employerName={employerName}
          companyOverviewUrl={companyOverviewUrl}
          isNonZorroEnrollmentAgent={isNonZorroEnrollmentAgent}
        />
        <Group justify="space-between">
          <Text c="zorroGray.4" size="xs">
            Role
          </Text>
          <Group gap="xxs">
            <Text size="xs">{getRoleLabel(role)}</Text>
            <Button
              variant="tertiary"
              size="xs"
              leftSection={<IconPencil />}
              onClick={() => {
                void sendEvent('click_edit_role');
                setAction(EmployeeLayoutAction.EDIT_EMPLOYEE_ROLE);
              }}
            />
          </Group>
        </Group>
        <Group justify="space-between">
          <Text c="zorroGray.4" size="xs">
            Employee ID
          </Text>
          <Group gap="xxs">
            <Text size="xs">{employee.idFromEmployer || '-'}</Text>
            <Button
              variant="tertiary"
              size="xs"
              leftSection={<IconPencil />}
              onClick={() => {
                void sendEvent('click_edit_employee_id');
                setAction(EmployeeLayoutAction.EDIT_ID_FROM_EMPLOYER);
              }}
            />
          </Group>
        </Group>
        {employerSubEntities.length > 0 && (
          <Group justify="space-between">
            <Text c="zorroGray.4" size="xs">
              {getSubEntityLabel()}
            </Text>
            <Group gap="xxs" maw="50%" justify="flex-end" wrap="nowrap">
              <Tooltip label={subEntity?.name} disabled={!subEntity?.name}>
                <Text size="xs" truncate="end">
                  {subEntity?.name || '-'}
                </Text>
              </Tooltip>
              <Button
                variant="tertiary"
                size="xs"
                leftSection={<IconPencil />}
                onClick={() => {
                  void sendEvent('click_edit_subentity');
                  setAction(EmployeeLayoutAction.EDIT_SUB_ENTITY);
                }}
              />
            </Group>
          </Group>
        )}
        {isSubEntity2Enabled && (
          <Group justify="space-between">
            <Text c="zorroGray.4" size="xs">
              Sub-entity 2
            </Text>
            <Group gap="xxs" maw="50%" justify="flex-end" wrap="nowrap">
              <Tooltip label={subEntity2?.name} disabled={!subEntity2?.name}>
                <Text size="xs" truncate="end">
                  {subEntity2?.name || '-'}
                </Text>
              </Tooltip>
              <Button
                variant="tertiary"
                size="xs"
                leftSection={<IconPencil />}
                onClick={() => {
                  void sendEvent('click_edit_subentity_2');
                  setAction(EmployeeLayoutAction.EDIT_SUB_ENTITY_2);
                }}
              />
            </Group>
          </Group>
        )}
      </Stack>
      <EligibilityStatusBadge setAction={setAction} employeeId={employeeId} />
      {!isNonZorroEnrollmentAgent && (
        <Button
          mt="xl"
          variant="tertiary"
          leftSection={<IconChevronLeft size={20} />}
          onClick={() => {
            void sendEvent('click_back_to_employees');
            if (isDon) {
              navigateToRoute('DON_EMPLOYEES', [{}]);
            } else {
              navigateToRoute('OPS_EMPLOYERS_EMPLOYEES', [{ employerId }]);
            }
          }}
        >
          Back to all employees
        </Button>
      )}
    </Stack>
  );
};
